import React from 'react';
import classNames from 'classnames';
import { round } from 'lodash';
import { Box, Chip, Typography } from '@mui/material';
import { SwellArrowIcon } from '@wavetrak/theme';

import TideArrow from 'components/Icons/TideArrow';
import type {
  CamForecastConditions,
  CamForecastConditionsSwell,
  CameraConditionsUnits,
} from 'types/camera';

import {
  formatKjDeltaPct,
  formatPowerKj,
  formatSwellDirection,
  formatSwellHeight,
  formatSurfHeightRange,
  formatSurfHeightUnit,
  formatTideTrend,
  formatWindDirectionLabel,
  formatWindSpeed,
  formatWindSubtext,
  formatWindUnit,
} from './formatCamMatchConditions';
import styles from './CamMatchesForecastModal.module.scss';

const KJ_BADGE_POSITIVE_COLOR = '#00ff88';
const KJ_BADGE_NEGATIVE_COLOR = '#FF8080';
/** Matches .kjBadge label font-size (10px); width preserves TideArrow viewBox aspect ratio (24:20). */
const KJ_BADGE_ICON_HEIGHT = 10;
const KJ_BADGE_ICON_WIDTH = 12;

export interface CamMatchesForecastComparisonCardsProps {
  camConditions: CamForecastConditions | undefined;
  forecastConditions: CamForecastConditions | undefined;
  units: CameraConditionsUnits | undefined;
  swellSimilarity: number;
  windSimilarity: number;
  tideSimilarity: number;
  isLoading: boolean;
}

export const CamMatchesForecastComparisonCards: React.FC<
  CamMatchesForecastComparisonCardsProps
> = ({
  camConditions,
  forecastConditions,
  units,
  swellSimilarity,
  windSimilarity,
  tideSimilarity,
  isLoading,
}) => {
  const waveHeightUnit = units?.waveHeight ?? 'FT';
  const windSpeedUnit = units?.windSpeed ?? 'MPH';
  const tideHeightUnit = units?.tideHeight ?? 'FT';

  const kjDeltaPct =
    camConditions && forecastConditions
      ? formatKjDeltaPct(camConditions.power, forecastConditions.power)
      : null;

  return (
    <Box className={styles.cards}>
      <ComparisonCard>
        <ComparisonColumn title="Surf height">
          <SurfHeightBody
            conditions={camConditions}
            isLoading={isLoading}
            waveHeightUnit={waveHeightUnit}
          />
        </ComparisonColumn>
        <ComparisonDivider />
        <ComparisonColumn
          title="Surf height"
          badge={kjDeltaPct != null && kjDeltaPct !== 0 ? <KjDeltaBadge pct={kjDeltaPct} /> : null}
        >
          <SurfHeightBody
            conditions={forecastConditions}
            isLoading={isLoading}
            waveHeightUnit={waveHeightUnit}
          />
        </ComparisonColumn>
      </ComparisonCard>

      <ComparisonCard tall>
        <ComparisonColumn title="Swell">
          <SwellBody
            conditions={camConditions}
            isLoading={isLoading}
            waveHeightUnit={waveHeightUnit}
          />
        </ComparisonColumn>
        <ComparisonDivider />
        <ComparisonColumn
          title="Swell"
          badge={<MatchBadge pct={swellSimilarity} testId="cam-matches-forecast-swell-match" />}
        >
          <SwellBody
            conditions={forecastConditions}
            isLoading={isLoading}
            waveHeightUnit={waveHeightUnit}
          />
        </ComparisonColumn>
      </ComparisonCard>

      <ComparisonCard>
        <ComparisonColumn title="Wind">
          <WindBody
            conditions={camConditions}
            isLoading={isLoading}
            windSpeedUnit={windSpeedUnit}
          />
        </ComparisonColumn>
        <ComparisonDivider />
        <ComparisonColumn
          title="Wind"
          badge={<MatchBadge pct={windSimilarity} testId="cam-matches-forecast-wind-match" />}
        >
          <WindBody
            conditions={forecastConditions}
            isLoading={isLoading}
            windSpeedUnit={windSpeedUnit}
          />
        </ComparisonColumn>
      </ComparisonCard>

      <ComparisonCard>
        <ComparisonColumn title="Tide">
          <TideBody
            conditions={camConditions}
            isLoading={isLoading}
            tideHeightUnit={tideHeightUnit}
          />
        </ComparisonColumn>
        <ComparisonDivider />
        <ComparisonColumn
          title="Tide"
          badge={<MatchBadge pct={tideSimilarity} testId="cam-matches-forecast-tide-match" />}
        >
          <TideBody
            conditions={forecastConditions}
            isLoading={isLoading}
            tideHeightUnit={tideHeightUnit}
          />
        </ComparisonColumn>
      </ComparisonCard>
    </Box>
  );
};

function ComparisonCard({ children, tall = false }: { children: React.ReactNode; tall?: boolean }) {
  return (
    <Box
      component="article"
      className={tall ? styles.comparisonCardTall : styles.comparisonCard}
      data-testid="cam-matches-forecast-comparison-card"
    >
      {children}
    </Box>
  );
}

function ComparisonColumn({
  title,
  badge,
  children,
}: {
  title: string;
  badge?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <Box className={styles.comparisonCol}>
      <Box className={styles.comparisonHeader}>
        <Typography component="h3" className={styles.comparisonTitle}>
          {title}
        </Typography>
        {badge}
      </Box>
      {children}
    </Box>
  );
}

function ComparisonDivider() {
  return <Box aria-hidden className={styles.comparisonDivider} />;
}

function KjDeltaBadge({ pct }: { pct: number }) {
  const magnitude = Math.abs(pct);
  const isPositive = pct > 0;
  const direction = isPositive ? 'rising' : 'falling';
  const comparison = isPositive ? 'higher' : 'lower';
  const accentColor = isPositive ? KJ_BADGE_POSITIVE_COLOR : KJ_BADGE_NEGATIVE_COLOR;

  return (
    <Chip
      icon={
        <Box component="span" aria-hidden className={styles.kjBadgeIcon}>
          <TideArrow
            direction={direction}
            fill={accentColor}
            height={KJ_BADGE_ICON_HEIGHT}
            width={KJ_BADGE_ICON_WIDTH}
          />
        </Box>
      }
      label={`${magnitude}% kJ`}
      size="small"
      className={classNames(
        styles.kjBadge,
        isPositive ? styles.kjBadgePositive : styles.kjBadgeNegative,
      )}
      aria-label={`Forecast energy ${magnitude} percent ${comparison} than cam match`}
      data-testid="cam-matches-forecast-kj-badge"
    />
  );
}

function MatchBadge({ pct, testId }: { pct: number; testId: string }) {
  return (
    <Chip
      label={`${Math.round(pct)}% match`}
      size="small"
      className={styles.matchBadge}
      data-testid={testId}
    />
  );
}

function LoadingPlaceholder({ lines = 1 }: { lines?: number }) {
  return (
    <Box className={styles.loadingBlock} aria-hidden>
      {Array.from({ length: lines }, (_, i) => (
        <Box key={i} className={styles.loadingLine} />
      ))}
    </Box>
  );
}

function SurfHeightBody({
  conditions,
  isLoading,
  waveHeightUnit,
}: {
  conditions: CamForecastConditions | undefined;
  isLoading: boolean;
  waveHeightUnit: CameraConditionsUnits['waveHeight'];
}) {
  if (isLoading && !conditions) return <LoadingPlaceholder lines={2} />;
  if (!conditions) {
    return (
      <Typography component="span" className={styles.readingMuted}>
        —
      </Typography>
    );
  }

  const height = formatSurfHeightRange(conditions.surf, waveHeightUnit);
  const unit = formatSurfHeightUnit(conditions.surf, waveHeightUnit);

  return (
    <Box className={styles.readingBlock}>
      <Typography component="p" className={styles.primaryReading}>
        <Typography component="span" className={styles.primaryValue}>
          {height}
        </Typography>
        {unit ? (
          <Typography component="span" className={styles.primaryUnit}>
            {unit}
          </Typography>
        ) : null}
        {conditions.surf.plus ? (
          <Typography component="span" className={styles.primaryUnit}>
            +
          </Typography>
        ) : null}
      </Typography>
      <Typography component="p" className={styles.secondaryReading}>
        {formatPowerKj(conditions.power)}
      </Typography>
    </Box>
  );
}

function SwellBody({
  conditions,
  isLoading,
  waveHeightUnit,
}: {
  conditions: CamForecastConditions | undefined;
  isLoading: boolean;
  waveHeightUnit: CameraConditionsUnits['waveHeight'];
}) {
  if (isLoading && !conditions) return <LoadingPlaceholder lines={3} />;
  if (!conditions?.swells?.length) {
    return (
      <Typography component="span" className={styles.readingMuted}>
        —
      </Typography>
    );
  }

  return (
    <Box className={styles.swellList}>
      {conditions.swells.map((swell, index) => (
        <SwellRow key={swellKey(swell, index)} swell={swell} waveHeightUnit={waveHeightUnit} />
      ))}
    </Box>
  );
}

function SwellRow({
  swell,
  waveHeightUnit,
}: {
  swell: CamForecastConditionsSwell;
  waveHeightUnit: CameraConditionsUnits['waveHeight'];
}) {
  const { cardinal, degrees } = formatSwellDirection(swell);

  return (
    <Box className={styles.swellRow}>
      <Typography component="span" className={styles.swellHeight}>
        {formatSwellHeight(swell.height, waveHeightUnit)}
      </Typography>
      <Typography component="span" className={styles.swellPeriod}>
        {`${round(swell.period)}s`}
      </Typography>
      <Box className={styles.swellDirectionContainer}>
        <Box className={styles.swellIconContainer}>
          <SwellArrowIcon size="small" rotation={swell.direction - 180} />
        </Box>
        <Typography component="span" className={styles.swellDirection}>
          {cardinal}
        </Typography>
        <Typography component="span" className={styles.swellDegrees}>
          {degrees}
        </Typography>
      </Box>
    </Box>
  );
}

function WindBody({
  conditions,
  isLoading,
  windSpeedUnit,
}: {
  conditions: CamForecastConditions | undefined;
  isLoading: boolean;
  windSpeedUnit: CameraConditionsUnits['windSpeed'];
}) {
  if (isLoading && !conditions) return <LoadingPlaceholder lines={2} />;
  if (!conditions) {
    return (
      <Typography component="span" className={styles.readingMuted}>
        —
      </Typography>
    );
  }

  const unit = formatWindUnit(windSpeedUnit);
  const direction = formatWindDirectionLabel(conditions.wind);

  return (
    <Box className={styles.readingBlock}>
      <Typography component="p" className={styles.primaryReading}>
        <Typography component="span" className={styles.primaryValue}>
          {formatWindSpeed(conditions.wind.speed)}
        </Typography>
        <Typography component="span" className={styles.primaryUnit}>
          {unit} {direction}
        </Typography>
      </Typography>
      <Typography component="p" className={styles.secondaryReading}>
        {formatWindSubtext(conditions.wind, windSpeedUnit)}
      </Typography>
    </Box>
  );
}

function TideBody({
  conditions,
  isLoading,
  tideHeightUnit,
}: {
  conditions: CamForecastConditions | undefined;
  isLoading: boolean;
  tideHeightUnit: CameraConditionsUnits['tideHeight'];
}) {
  if (isLoading && !conditions) return <LoadingPlaceholder lines={1} />;
  if (!conditions) {
    return (
      <Typography component="span" className={styles.readingMuted}>
        —
      </Typography>
    );
  }

  const trend = formatTideTrend(conditions.tide.trend);
  const heightValue = conditions.tide.height;
  const display =
    heightValue != null && Number.isFinite(heightValue)
      ? heightValue >= 10
        ? round(heightValue, 0)
        : round(heightValue, 1)
      : '—';
  const unit = tideHeightUnit === 'M' ? 'm' : 'ft';

  return (
    <Box className={styles.readingBlock}>
      <Box className={styles.tideRow}>
        <Typography component="p" className={styles.primaryReading}>
          <Typography component="span" className={styles.primaryValue}>
            {display}
          </Typography>
          <Typography component="span" className={styles.primaryUnit}>
            {unit}
          </Typography>
        </Typography>
        {trend ? <TideArrow direction={trend} height={13} width={12} /> : null}
      </Box>
    </Box>
  );
}

function swellKey(swell: CamForecastConditionsSwell, index: number): string {
  return `${index}-${swell.height}-${swell.period}-${swell.direction}`;
}
